/**
 * SDK Error handling
 * Step 7: Standardized error codes and SDKError class
 * V1.1: Added tryModelIds for graceful degradation suggestions
 * V1.2: Added MANIFEST_SIGNATURE_INVALID for registry verification
 */
/**
 * Standardized SDK error with code, message, and optional cause.
 * All major failure points in the SDK throw this error type.
 */
export class SDKError extends Error {
    constructor(code, message, cause, options) {
        super(message);
        this.name = 'SDKError';
        this.code = code;
        this.cause = cause;
        this.tryModelIds = options?.tryModelIds;
        this.failedModelId = options?.failedModelId;
        // Maintain proper stack trace in V8 environments (V8/Chrome/Node)
        const ErrorWithCapture = Error;
        if (typeof ErrorWithCapture.captureStackTrace === 'function') {
            ErrorWithCapture.captureStackTrace(this, SDKError);
        }
    }
    /**
     * Create a user-friendly error message with actionable hints.
     */
    toUserMessage() {
        switch (this.code) {
            case 'NO_WEBGPU':
                return 'WebGPU is not available. Please use Chrome 113+, Edge 113+, or another WebGPU-enabled browser.';
            case 'WEBGPU_INIT_FAILED':
                return 'Failed to initialize WebGPU. Your GPU may not be supported or WebGPU may be disabled.';
            case 'MANIFEST_LOAD_ERROR':
                return `Failed to load model manifest. Check that the manifest URL is correct and accessible.`;
            case 'MANIFEST_INVALID':
                return 'The model manifest is invalid. Check the manifest format and model definitions.';
            case 'MANIFEST_SIGNATURE_INVALID':
                return 'Manifest signature verification failed. The manifest may have been tampered with or the public key is incorrect.';
            case 'MODEL_NOT_FOUND':
                return `Model not found. Check that the model ID exists in the manifest.`;
            case 'MODEL_LOAD_ERROR': {
                let msg = 'Failed to load model. The model may be unavailable or your device may not have enough memory.';
                if (this.tryModelIds && this.tryModelIds.length > 0) {
                    msg += ` Try a smaller model: ${this.tryModelIds.join(', ')}`;
                }
                return msg;
            }
            case 'INFERENCE_ERROR':
                return 'Inference failed. Try reloading the model or using a smaller model.';
            case 'ABORTED':
                return 'Operation was cancelled.';
            case 'CONFIG_ERROR':
                return 'Invalid SDK configuration. Check your client configuration options.';
            default:
                return this.message;
        }
    }
}
/**
 * Type guard to check if an error is an SDKError.
 */
export function isSDKError(error) {
    return error instanceof SDKError;
}
/**
 * Wrap an unknown error as SDKError if it isn't already.
 */
export function wrapError(code, error, fallbackMessage) {
    if (isSDKError(error)) {
        return error;
    }
    const message = error instanceof Error
        ? error.message
        : fallbackMessage ?? String(error);
    return new SDKError(code, message, error);
}
//# sourceMappingURL=errors.js.map